<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGlobalStore } from '@/stores/globalStore'
import { useAuth } from '@/lib/utils/useAuth'
import { getUserProfile } from '@/lib/utils/useCommonUtil'
import type { MenuItem } from 'primevue/menuitem'

const globalStore = useGlobalStore()
const auth = useAuth()
const router = useRouter()
const { t } = useI18n()

const menu = ref()
const profile = computed(() => getUserProfile(globalStore.user))

const items = computed<MenuItem[]>(() => [
  {
    // Profile lives inside Settings (Configs route) as the first
    // section. The deep-link query targets that section directly so
    // the dropdown lands the operator on their own identity form
    // without an extra click.
    label: t('nav.profile'),
    icon: 'pi pi-user',
    command: () => router.push({ name: 'Configs', query: { section: 'profile' } }),
  },
  { separator: true },
  {
    label: t('nav.logout'),
    icon: 'pi pi-sign-out',
    command: async () => {
      await auth.attemptLogout()
      void router.push({ name: 'Login' })
    },
  },
])

const open = (event: Event) => menu.value?.toggle(event)
</script>

<template>
  <div v-if="globalStore.user.detail" class="flex items-center">
    <button
      type="button"
      class="flex items-center rounded-full focus-visible:outline-none"
      :aria-label="profile.label"
      :title="profile.label"
      aria-haspopup="true"
      aria-controls="user-menu"
      @click="open"
    >
      <Avatar
        :label="profile.initials"
        shape="circle"
        size="normal"
        class="bg-accent-100 text-accent-700"
      />
    </button>
    <Menu id="user-menu" ref="menu" :model="items" popup />
  </div>
</template>
